import {
  addToast,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Select,
  SelectItem,
} from "@heroui/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { years, fetchCursos } from "@/lib/utils";
import { useMask } from "@react-input/mask";
import { useEffect, useState, useRef } from "react";
import { CalendarDate } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { IconFile } from "@tabler/icons-react";
import { apiFetch } from "@/lib/api";

interface LocalData {
  contato: string;
  nome: string;
  email: string;
  curso: string;
  data_nascimento: string;
  ano_inicio: string;
  ano_fim: string;
  periodo: string;
}

interface Cursos {
  nome: string;
  id_curso: string;
  descricao: string;
  habilitado: boolean;
  lista_periodos: string[];
}

export default function ProfileModal() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const modal = searchParams.get("modal");
  const { data: session, status, update } = useSession();
  const [invalidPhone, setInvalidPhone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [cursos, setCursos] = useState<Cursos[]>([]);
  const [cursoSelecionado, setCursoSelecionado] = useState<Cursos | null>(null);
  const [localData, setLocalData] = useState<LocalData>({
    contato: "",
    nome: "",
    email: "",
    curso: "",
    data_nascimento: "2000-01-01",
    ano_inicio: "",
    ano_fim: "",
    periodo: "",
  });

  // Preenche localData com dados da sessão
  useEffect(() => {
    if (status !== "authenticated" || !session?.user) return;

    setLocalData({
      contato: session.user.contato ?? "",
      nome: session.user.nome ?? "",
      email: session.user.email ?? "",
      curso: session.user.curso ?? "",
      data_nascimento: session.user.data_nascimento ?? "2000-01-01",
      ano_inicio: String(session.user.ano_inicio ?? ""),
      ano_fim: String(session.user.ano_fim ?? ""),
      periodo: String(session.user.periodo ?? ""),
    });
  }, [session, status]);

  // Carrega cursos da API
  useEffect(() => {
    if (status !== "authenticated" || !session?.accessToken) return;

    const loadCursos = async () => {
      const res = await fetchCursos(session.accessToken, setCursos);
      if (res !== null) {
        addToast({
          title: res.title,
          description: res.description,
          color: "danger",
        });
      }
    };

    loadCursos();
  }, [status, session]);

  // Pré-seleciona o curso com base na session
  useEffect(() => {
    if (!session?.user?.curso) return;
    if (cursos.length === 0) return;

    const curso = cursos.find((c) => c.id_curso === session.user.curso);

    if (curso) {
      setCursoSelecionado(curso);
      setLocalData((prev) => ({
        ...prev,
        curso: curso.id_curso,
      }));
    }
  }, [session, cursos]);

  const toCalendarDate = (input: string | null) => {
    if (!input) return null;

    const [yyyy, mm, dd] = input.split("-").map(Number);

    if (!yyyy || !mm || !dd) return null;

    return new CalendarDate(yyyy, mm, dd);
  };

  const handlePhoneValidation = (value: string) => {
    if (value.length > 0 && value.length < 15) {
      setInvalidPhone(true);
    } else {
      setInvalidPhone(false);
    }
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setIsLoading(true);

    setFileName(file.name);
    setPreview(URL.createObjectURL(file));

    setIsLoading(false);
  };

  const handleImageUpload = async (
    file: File | null,
  ): Promise<string | undefined> => {
    if (!file || file.size === 0) return undefined;

    const fd = new FormData();
    fd.append("imagem", file, file.name);

    const resImage = await apiFetch("/users/me/avatar", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.accessToken}` },
      body: fd,
    });

    if (!resImage.ok) {
      addToast({
        title: "Erro ao enviar imagem",
        description: "Não foi possível atualizar a imagem de perfil",
        color: "danger",
      });
      return undefined;
    }

    const imageData = await resImage.json();
    return imageData.url as string;
  };

  const handleEditProfileModal = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const file = formData.get("imagem") as File | null;

    const imageUrl = await handleImageUpload(file);

    const payload: any = {
      nome: formData.get("name") as string,
      email: formData.get("email") as string,
      contato: formData.get("contato") as string,
      curso: formData.get("curso") as string,
      data_nascimento: formData.get("data_nascimento") as string,
      ano_inicio: formData.get("ano_inicio")
        ? Number(formData.get("ano_inicio"))
        : null,
      ano_fim: formData.get("ano_fim") ? Number(formData.get("ano_fim")) : null,
      periodo: formData.get("periodo") as string,
    };

    if (imageUrl) {
      payload.imagem = imageUrl;
    }

    setIsLoading(true);

    const res = await apiFetch("/auth/register/step2", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      addToast({
        title: "Perfil atualizado",
        description: "Seu perfil foi atualizado com sucesso!",
        color: "success",
      });

      setIsLoading(false);

      await update();
      router.push(pathname);
    } else {
      addToast({
        title: "Erro ao atualizar perfil",
        description: "Tente novamente mais tarde",
        color: "danger",
      });
      setIsLoading(false);
      router.refresh();
    }
  };

  const phoneRef = useMask({
    mask: "($$) $$$$$-$$$$",
    replacement: { $: /\d/ },
  });

  const onOpenChange = () => {
    router.push(pathname);
  };

  return (
    <Modal
      isOpen={modal === "editProfile"}
      onOpenChange={onOpenChange}
      backdrop="blur"
      placement="center"
      className="md:min-w-1/2"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Edite seu perfil</ModalHeader>
            <ModalBody>
              <Form
                className="flex gap-4 flex-col"
                onSubmit={handleEditProfileModal}
              >
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
                  <div className="flex gap-2 flex-col w-full justify-center items-center max-w-1/2">
                    {preview ? (
                      <Image
                        width={200}
                        height={200}
                        quality={100}
                        src={
                          preview || session?.user?.image || "/defaultUser.jpg"
                        }
                        alt="Preview da imagem de perfil"
                        className="object-cover rounded-full border w-20 h-20"
                      />
                    ) : (
                      <div
                        className={`w-20 h-20 flex items-center justify-center bg-gray-200 rounded-full ${!session?.user?.image && "border"}`}
                      >
                        <Image
                          src={session?.user?.image || "/defaultUser.jpg"}
                          width={200}
                          height={200}
                          quality={100}
                          alt="Preview da imagem de perfil"
                          className="object-cover rounded-full border w-20 h-20"
                        />
                      </div>
                    )}
                    <p className="text-black text-sm text-center wrap-break-word whitespace-normal max-w-1/2">
                      {fileName || "Escolha uma imagem"}
                    </p>
                  </div>
                  <Button
                    className="w-full text-black cursor-pointer max-w-[50%]"
                    color="primary"
                    variant="flat"
                    onPress={() => {
                      fileRef.current?.click();
                    }}
                    startContent={<IconFile size={25} />}
                    isLoading={isLoading}
                  >
                    Enviar Imagem
                  </Button>
                  <input
                    ref={fileRef}
                    name="imagem"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleSelect}
                  />
                </div>

                <div className="flex gap-3 min-w-full">
                  <Input
                    type="text"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setLocalData({ ...localData, nome: e.target.value });
                    }}
                    value={localData.nome}
                    name="name"
                    label="Nome completo"
                    placeholder="John Doe"
                    isRequired
                  />
                  <I18nProvider locale="pt-BR">
                    <DatePicker
                      name="data_nascimento"
                      label="Data de nascimento"
                      showMonthAndYearPickers
                      value={toCalendarDate(localData.data_nascimento)}
                      placeholderValue={new CalendarDate(2000, 1, 1)}
                      isRequired
                      variant="bordered"
                      onChange={(date) => {
                        if (!date) {
                          setLocalData((prev) => ({
                            ...prev,
                            data_nascimento: "",
                          }));
                          return;
                        }

                        const yyyy = date.year.toString().padStart(4, "0");
                        const mm = date.month.toString().padStart(2, "0");
                        const dd = date.day.toString().padStart(2, "0");

                        setLocalData((prev) => ({
                          ...prev,
                          data_nascimento: `${yyyy}-${mm}-${dd}`,
                        }));
                      }}
                    />
                  </I18nProvider>
                </div>

                <Select
                  name="curso"
                  label="Curso"
                  placeholder="Selecione seu curso"
                  selectedKeys={localData.curso ? [localData.curso] : []} // id_curso
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0] as string;
                    const curso =
                      cursos.find((c) => c.id_curso === key) || null;

                    setCursoSelecionado(curso);
                    setLocalData((prev) => ({ ...prev, curso: key }));
                  }}
                  isRequired
                >
                  {cursos.map((curso) => (
                    <SelectItem key={curso.id_curso}>{curso.nome}</SelectItem>
                  ))}
                </Select>

                <Select
                  name="periodo"
                  placeholder="Selecione o período"
                  label="Período"
                  selectedKeys={localData.periodo ? [localData.periodo] : []}
                  isRequired
                  isDisabled={!cursoSelecionado}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0] as string; // "1", "2", "3", ...
                    setLocalData((prev) => ({ ...prev, periodo: key }));
                  }}
                >
                  {cursoSelecionado?.lista_periodos.map(
                    (periodoLabel, index) => (
                      <SelectItem key={String(index + 1)}>
                        {periodoLabel}
                      </SelectItem>
                    ),
                  )}
                </Select>

                <div className="min-w-full gap-3 flex">
                  <Input
                    type="email"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setLocalData({ ...localData, email: e.target.value });
                    }}
                    value={localData.email}
                    name="email"
                    label="Email"
                    placeholder="Email"
                    isRequired
                  />
                  <Input
                    name="contato"
                    ref={phoneRef}
                    minLength={15}
                    isRequired
                    label="Contato"
                    errorMessage={"Numero de telefone invalido"}
                    placeholder="(27) 99999-8888"
                    type="text"
                    inputMode="numeric"
                    isInvalid={invalidPhone}
                    value={localData.contato}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setLocalData({ ...localData, contato: e.target.value });
                      handlePhoneValidation(e.target.value);
                    }}
                  />
                </div>

                <div className="flex min-w-full gap-3">
                  <Select
                    name="ano_inicio"
                    placeholder="Selecione o ano"
                    label="Ano de início"
                    defaultSelectedKeys={
                      localData.ano_inicio ? [localData.ano_inicio] : []
                    }
                    isRequired
                  >
                    {years.map((year) => (
                      <SelectItem key={year.key}>{year.label}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    name="ano_fim"
                    placeholder="Selecione o ano"
                    label="Ano de término"
                    defaultSelectedKeys={
                      localData.ano_fim ? [localData.ano_fim] : []
                    }
                    isRequired
                  >
                    {years.map((year) => (
                      <SelectItem key={year.key}>{year.label}</SelectItem>
                    ))}
                  </Select>
                </div>

                <div className="min-w-full flex justify-end gap-2">
                  <Button
                    type="button"
                    color="danger"
                    onPress={onClose}
                    isLoading={isLoading}
                  >
                    Fechar
                  </Button>
                  <Button type="submit" color="success" isLoading={isLoading}>
                    Enviar
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
